// src/pages/CallBulkUpload/CallBulkUpload.jsx
// Bulk upload call logs via Excel
// Template columns: empCode | mobile | empEmail | callKey | status | callDate
//                   outcome | remarks | nextAction | q1..q12 (day10 only)

import React, { useState, useMemo } from "react";
import { useAllSchedules } from "../../context/MasterDataContext";
import { useNavigate }     from "react-router-dom";
import {
  Button, Table, Tag, Steps, Alert, Select,
  message as antMsg, Tooltip,
} from "antd";
import {
  ArrowLeftOutlined, DownloadOutlined, PhoneOutlined,
  FileExcelOutlined, EyeOutlined, WarningOutlined,
  CheckCircleOutlined, InfoCircleOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { addLog } from '../Admin/SystemLogPage';
import dayjs from "dayjs";
import api from "../../services/api";

const C = {
  navy:"#1B2D6B", blue:"#1E6FBF", green:"#16a34a",
  red:"#dc2626", amber:"#b45309", purple:"#7c3aed",
  text1:"#1a2d4a", text2:"#4a6580", text3:"#7a96b0",
  border:"#dce6f0", bg:"#f5f8fc",
};

const STATUSES   = ["Completed","Not Reachable","Call Later","No Response","NA"];
// NA = Not Applicable — this call stage does not apply to this employee
// NA calls are excluded from pending counts everywhere in the system
const DAY10_QS   = ["q1","q2","q3","q4","q5","q6","q7","q8","q9","q10","q11","q12"];

// Q3 & Q8 = rating 1-5 | Q10 = choice | rest = Yes/No/NA
const Q_ANSWER_TYPE = { q3:"rating5", q8:"rating5", q10:"choice" };
const Q10_CHOICES_LOWER = ["daily", "call by call"];  // case-insensitive match
// Rating: numeric 1-5, allows decimals like 3.5, 4.0
function isValidRating(v) {
  const n = parseFloat(v);
  return !isNaN(n) && n >= 1 && n <= 5;
}
// Normalise Q10 input to canonical form
function normaliseQ10(v) {
  const l = v.trim().toLowerCase().replace(/[^a-z ]/g,"");
  if (l === "daily")                             return "Daily";
  if (l === "call by call" || l === "callbycall") return "Call by Call";
  return v; // return as-is so error message shows original
}

function safeStr(v) {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "object" && v.text   != null) return String(v.text).trim();
  if (typeof v === "object" && v.result != null) return String(v.result).trim();
  if (Array.isArray(v?.richText)) return v.richText.map(r=>r.text||"").join("").trim();
  return String(v).trim();
}

function parseDate(v) {
  if (!v) return dayjs().format("YYYY-MM-DD");
  if (v instanceof Date) return dayjs(v).format("YYYY-MM-DD");
  if (typeof v === "number")
    return dayjs(new Date(Math.round((v-25569)*86400*1000))).format("YYYY-MM-DD");
  const s = safeStr(v);
  const d = dayjs(s);
  return d.isValid() ? d.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
}

// ─────────────────────────────────────────────────────────────────
export default function CallBulkUpload() {
  const navigate = useNavigate();

  const [step,    setStep]   = useState(0);
  const [file,    setFile]   = useState(null);
  const [parsing, setParsing]= useState(false);
  const [rows,    setRows]   = useState([]);
  const [loading, setLoading]= useState(false);
  const [result,  setResult] = useState(null);
  const [error,   setError]  = useState("");
  const [filterKey, setFilterKey] = useState("all");

  // ── Per-cohort call keys (dynamic from master data) ─────────
  const allSchedules = useAllSchedules();

  // Union of all valid callKeys across ALL cohort types
  const CALL_KEYS = useMemo(() => {
    const seen = new Set();
    const keys = [];
    allSchedules.forEach(sched =>
      sched.stages?.forEach(s => { if (!seen.has(s.key)) { seen.add(s.key); keys.push(s.key); } })
    );
    // Sort by day number
    return keys.sort((a,b) => {
      const da = allSchedules.flatMap(s=>s.stages).find(s=>s.key===a)?.day ?? 0;
      const db = allSchedules.flatMap(s=>s.stages).find(s=>s.key===b)?.day ?? 0;
      return da - db;
    });
  }, [allSchedules]);

  // Map: callKey → which cohort types it belongs to (for display)
  const keyScheduleMap = useMemo(() => {
    const map = {};
    allSchedules.forEach(sched => {
      sched.stages?.forEach(s => {
        if (!map[s.key]) map[s.key] = [];
        map[s.key].push(sched.label);
      });
    });
    return map;
  }, [allSchedules]);

  // Map: callKey → day number
  const keyDayMap = useMemo(() => {
    const map = {};
    allSchedules.forEach(sched =>
      sched.stages?.forEach(s => { map[s.key] = s.day; })
    );
    return map;
  }, [allSchedules]);

  // Which callKey has Q&A (hasQuestions=true)
  const keysWithQuestions = useMemo(() => {
    const set = new Set();
    allSchedules.forEach(sched =>
      sched.stages?.forEach(s => { if (s.hasQuestions) set.add(s.key); })
    );
    return set;
  }, [allSchedules]);

  // ── Parse uploaded file ──────────────────────────────────────
  function parseFile(f) {
    setParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb  = XLSX.read(e.target.result, { type:"array", cellDates:true });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval:"", raw:false });

        const parsed = raw.map((r, idx) => {
          const empCode    = safeStr(r.empCode    || r.EmpCode    || r.emp_code || "").toUpperCase();
          const mobile     = safeStr(r.mobile     || r.Mobile     || "");
          const empEmail   = safeStr(r.empEmail   || r.Email      || r.email   || "").toLowerCase();
          const callKey    = safeStr(r.callKey    || r.CallKey    || r.call_key|| "").toLowerCase().replace(/\s/g,"");
          const status     = safeStr(r.status     || r.Status     || "Completed");
          const callDate   = parseDate(r.callDate  || r.CallDate  || r.call_date || "");
          const outcome    = safeStr(r.outcome    || r.Outcome    || "");
          const remarks    = safeStr(r.remarks    || r.Remarks    || "");
          const nextAction = safeStr(r.nextAction || r.NextAction || r.next_action || "");

          // Day-10 answers
          const answers = {};
          DAY10_QS.forEach(q => {
            let v = safeStr(r[q] || r[q.toUpperCase()] || "");
            if (!v) return;
            // Normalise Q10 to canonical casing
            if (q === "q10") v = normaliseQ10(v);
            answers[q] = v;
          });

          const errs = [];
          if (!empCode && !mobile && !empEmail) errs.push("Identifier required (empCode/mobile/email)");
          if (!callKey)  errs.push("callKey required");
          else if (!CALL_KEYS.includes(callKey)) {
            const scheduleInfo = (allSchedules||[]).map(s =>
              `${s.label||s.scheduleKey}: ${(s.stages||[]).map(st=>st.key).join(',')}`
            ).join(' · ');
            errs.push(`Invalid callKey "${callKey}" — ${scheduleInfo || 'no schedules configured'}`);
          }
          if (!status)   errs.push("status required");
          else if (!STATUSES.includes(status)) errs.push(`Invalid status "${status}"`);
          DAY10_QS.forEach(q => {
            const v = answers[q];
            // Empty / NA are always valid for any question type
            if (!v || v === "NA" || v === "na" || v === "N/A") return;
            const aType = Q_ANSWER_TYPE[q] || "yesno";
            if (aType==="rating5" && !isValidRating(v))
              errs.push(`${q}: rating must be 1-5 (decimals ok, e.g. 3.5) or NA (got "${v}")`);
            else if (aType==="choice" && !Q10_CHOICES_LOWER.includes(v.toLowerCase().trim()))
              errs.push(`${q}: must be "Daily" or "Call by Call" (case-insensitive) or NA (got "${v}")`);
            else if (aType==="yesno" && !["Yes","No","NA"].includes(v))
              errs.push(`${q}: must be Yes/No/NA (got "${v}")`);
          });

          return {
            _row: idx+2, empCode, mobile, empEmail,
            callKey, status, callDate, outcome, remarks, nextAction,
            ...answers, _errors: errs,
          };
        }).filter(r => r.empCode||r.mobile||r.empEmail||r.callKey);

        setRows(parsed);
        setFile(f);
        setStep(1);
      } catch {
        antMsg.error("Failed to read file — ensure it is a valid Excel file");
      } finally { setParsing(false); }
    };
    reader.readAsArrayBuffer(f);
  }

  // ── Submit in batches of 100 rows ───────────────────────────
  async function handleSubmit() {
    const valid = rows.filter(r => !r._errors?.length);
    if (!valid.length) { antMsg.warning("No valid rows to process"); return; }
    setLoading(true); setError("");

    const BATCH_SIZE = 100;
    const batches = [];
    for (let i = 0; i < valid.length; i += BATCH_SIZE) {
      batches.push(valid.slice(i, i + BATCH_SIZE));
    }

    const aggregate = { totalRows: valid.length, success: 0, failed: 0, errors: [] };

    try {
      // api instance handles baseURL + auth token automatically
      for (let b = 0; b < batches.length; b++) {
        antMsg.loading({ content: `Processing batch ${b+1}/${batches.length}...`, key:"bulk-progress" });
        try {
          const res = await api.post("/calls/bulk-log", {
            rows: batches[b].map(({ _row, _errors, ...rest }) => rest),
          });
          const payload = res.data ?? res;
          aggregate.success += payload?.success ?? 0;
          aggregate.failed  += payload?.failed  ?? 0;
          aggregate.errors.push(...(payload?.errors ?? []));
        } catch (batchErr) {
          antMsg.destroy("bulk-progress");
          setError(batchErr?.response?.data?.message || batchErr.message || `Batch ${b+1} failed`);
          setStep(2);
          return;
        }
      }
      antMsg.destroy("bulk-progress");
      setResult(aggregate);
      setStep(2);
      antMsg.success(`Done — ${aggregate.success} calls logged`);
      addLog('UPLOAD','SUCCESS',`Call bulk upload completed — ${aggregate.success} logged, ${aggregate.failed} failed`, { totalRows: aggregate.totalRows, success: aggregate.success, failed: aggregate.failed, errors: aggregate.errors?.slice(0,5) });
    } catch(err) {
      antMsg.destroy("bulk-progress");
      setError(err.message||"Connection failed"); setStep(2);
      addLog('UPLOAD','ERROR','Call bulk upload failed', { error: err.message });
    } finally { setLoading(false); }
  }

  // ── Download templates ───────────────────────────────────────
  // ── Dynamic template download — driven by live schedules ─────
  function downloadTemplate(scheduleKey) {
    const sched = (allSchedules||[]).find(s =>
      s.scheduleKey === scheduleKey || s.label === scheduleKey
    );
    if (!sched) { antMsg.error('Schedule not found'); return; }

    const baseRow = (callKey, isQA=false) => {
      const base = {
        empCode: 'L04480', mobile: '', empEmail: '',
        callKey, status: 'Completed',
        callDate: dayjs().format('YYYY-MM-DD'),
        outcome: 'Connected', remarks: 'Employee doing well', nextAction: '',
      };
      if (isQA) {
        // Add Q&A columns dynamically from Q_ANSWER_TYPE overrides
        const qaRow = { ...base };
        ['q1','q2','q3','q4','q5','q6','q7','q8','q9','q10','q11','q12'].forEach(q => {
          const aType = Q_ANSWER_TYPE[q] || 'yesno';
          if (aType === 'rating5') qaRow[q] = '4';
          else if (aType === 'choice') qaRow[q] = 'Daily';
          else qaRow[q] = 'Yes';
        });
        return qaRow;
      }
      return base;
    };

    const data = (sched.stages||[]).map((stage, i) => {
      const empCode = `EMP${String(i+1).padStart(4,'0')}`;
      return baseRow(stage.key, !!stage.hasQuestions);
    });

    const filename = `CallBulkUpload_${sched.label.replace(/\s+/g,'_')}.xlsx`;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CallLog');
    XLSX.writeFile(wb, filename);
    antMsg.success(`${sched.label} template downloaded!`);
  }

  // Q&A-only template (all cohorts' Q&A stages)
  function downloadQATemplate() {
    const rows = (allSchedules||[]).flatMap(sched => {
      const qaStage = (sched.stages||[]).find(s => s.hasQuestions);
      if (!qaStage) return [];
      const row = {
        empCode: `EMP_${(sched.scheduleKey||'x').toUpperCase()}_001`,
        mobile: '', empEmail: '', callKey: qaStage.key,
        status: 'Completed',
        callDate: dayjs().format('YYYY-MM-DD'),
        outcome: 'Connected', remarks: '', nextAction: '',
      };
      ['q1','q2','q3','q4','q5','q6','q7','q8','q9','q10','q11','q12'].forEach(q => {
        const aType = Q_ANSWER_TYPE[q] || 'yesno';
        row[q] = aType==='rating5' ? '4' : aType==='choice' ? 'Daily' : 'Yes';
      });
      return [row];
    });
    if (!rows.length) { antMsg.warning('No Q&A stages configured'); return; }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'QA_CallLog');
    XLSX.writeFile(wb, 'CallBulkUpload_QA_All.xlsx');
    antMsg.success('Q&A template downloaded!');
  }

  // ── Download rows with errors as Excel ───────────────────────
  function downloadErrorFile() {
    const errorRows = rows.filter(r => r._errors?.length);
    if (!errorRows.length) { antMsg.warning("No error rows to download"); return; }
    const data = errorRows.map(({ _row, _errors, ...rest }) => ({
      Row:    _row,
      Errors: (_errors||[]).join(" | "),
      ...Object.fromEntries(
        Object.entries(rest).filter(([k]) => !k.startsWith("_"))
      ),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Errors");
    XLSX.writeFile(wb, "CallBulkUpload_Errors.xlsx");
    antMsg.success(`${errorRows.length} error rows exported`);
  }

  // ── Download server-side failed rows as Excel ─────────────────
  function downloadServerErrors() {
    if (!result?.errors?.length) { antMsg.warning("No failed rows"); return; }
    const data = result.errors.map(e => ({
      Row:        e.row,
      Identifier: e.identifier,
      Reason:     e.reason,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ServerErrors");
    XLSX.writeFile(wb, "CallBulkUpload_ServerErrors.xlsx");
    antMsg.success(`${result.errors.length} server error rows exported`);
  }

  // ── Filtered rows for preview ────────────────────────────────
  const filteredRows = filterKey === "all"
    ? rows
    : rows.filter(r => r.callKey === filterKey);

  const validCount   = rows.filter(r=>!r._errors?.length).length;
  const invalidCount = rows.filter(r=> r._errors?.length).length;

  // Count by callKey
  const keyCounts = CALL_KEYS.reduce((acc,k)=>({
    ...acc, [k]: rows.filter(r=>r.callKey===k).length
  }),{});

  // ── Preview columns ──────────────────────────────────────────
  const previewCols = [
    { title:"#",       dataIndex:"_row",     width:50,
      render:v=><span style={{color:C.text3,fontSize:11}}>{v}</span> },
    { title:"Emp Code",dataIndex:"empCode",  width:100,
      render:v=>v?<span style={{fontFamily:"monospace",fontWeight:700,color:C.navy}}>{v}</span>
               :<span style={{color:C.text3}}>—</span> },
    { title:"Mobile",  dataIndex:"mobile",   width:110,
      render:v=>v||<span style={{color:C.text3}}>—</span> },
    { title:"Call", dataIndex:"callKey", width:130,
      render:v=>{
        if (!v) return <Tag color="error">Missing</Tag>;
        const cohorts = keyScheduleMap[v] || [];
        return (
          <div style={{display:"flex",flexDirection:"column",gap:2}}>
            <Tag color="blue" style={{borderRadius:20,fontWeight:600,fontSize:11,margin:0}}>{v}</Tag>
            {cohorts.length>0&&(
              <span style={{fontSize:10,color:C.text3}}>{cohorts.join(" · ")}</span>
            )}
          </div>
        );
      }},
    { title:"Status",  dataIndex:"status",   width:120,
      render:v=>{
        const col = v==="Completed"?C.green:v==="No Response"?C.red:v==="NA"?C.text3:C.amber;
        return v
          ?<Tag style={{borderRadius:20,fontWeight:600,fontSize:11,
              color:col,background:col+"18",border:`1px solid ${col}44`}}>{v}</Tag>
          :<Tag color="error">Missing</Tag>;
      }},
    { title:"Call Date",dataIndex:"callDate",width:110,
      render:v=>v?<span style={{fontSize:12}}>{dayjs(v).format("DD MMM YYYY")}</span>:"—" },
    { title:"Outcome", dataIndex:"outcome",  width:110,
      render:v=>v||<span style={{color:C.text3}}>—</span> },
    { title:"Remarks", dataIndex:"remarks",  width:160, ellipsis:true,
      render:v=>v||<span style={{color:C.text3}}>—</span> },
    { title:"Q's", width:130, align:"center",
      render:(_,rec)=>{
        if (!keysWithQuestions.has(rec.callKey)) return <span style={{color:C.text3,fontSize:11}}>N/A</span>;
        const answered = DAY10_QS.filter(q=>rec[q]).length;
        const ratingOK = ["q3","q8"].every(q => !rec[q] || isValidRating(rec[q]));
        const choiceOK = !rec.q10 || Q10_CHOICES_LOWER.includes(rec.q10.toLowerCase().trim());
        return (
          <div style={{display:"flex",flexDirection:"column",gap:2,alignItems:"center"}}>
            <Tag color={answered===12?"success":"warning"} style={{margin:0}}>{answered}/12</Tag>
            {rec.q3  && <span style={{fontSize:9,color:ratingOK?"#16a34a":"#dc2626"}}>Q3:{rec.q3}/5</span>}
            {rec.q8  && <span style={{fontSize:9,color:ratingOK?"#16a34a":"#dc2626"}}>Q8:{rec.q8}/5</span>}
            {rec.q10 && <span style={{fontSize:9,color:choiceOK?"#1E6FBF":"#dc2626",maxWidth:90,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{rec.q10}</span>}
          </div>
        );
      }},
    { title:"Valid", dataIndex:"_errors", width:90, fixed:"right",
      render:e=>e?.length
        ?<Tooltip title={e.join(", ")}>
            <Tag color="error">✗ {e.length} err</Tag>
          </Tooltip>
        :<Tag color="success">✓ OK</Tag> },
  ];

  // ─────────────────────────────────────────────────────────────
  return (
    <div style={{maxWidth:1100,margin:"0 auto",padding:"0 4px 60px"}}>

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:24}}>
        <Button icon={<ArrowLeftOutlined />} onClick={()=>navigate("/employees")} />
        <div>
          <h2 style={{fontSize:20,fontWeight:700,color:C.text1,margin:0,
            display:"flex",alignItems:"center",gap:10}}>
            <PhoneOutlined style={{color:C.blue}}/> Call Bulk Upload
          </h2>
          <p style={{fontSize:13,color:C.text3,margin:0}}>
            Log multiple call records at once via Excel upload
          </p>
        </div>
      </div>

      {/* Steps */}
      <Steps current={step} style={{marginBottom:28}} items={[
        {title:"Select File",  description:"Upload Excel"},
        {title:"Preview",      description:"Review before logging"},
        {title:"Done",         description:"Upload summary"},
      ]} />

      {/* ══ STEP 0 — Upload ══ */}
      {step === 0 && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {/* Drop zone */}
          <div
            style={{border:`2px dashed ${C.border}`,borderRadius:16,background:"#fafcff",
              padding:"50px 40px",textAlign:"center",cursor:"pointer",transition:"all 0.2s"}}
            onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor=C.blue;}}
            onDragLeave={e=>{e.currentTarget.style.borderColor=C.border;}}
            onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor=C.border;
              const f=e.dataTransfer.files[0];if(f)parseFile(f);}}
            onClick={()=>document.getElementById("call-bulk-inp").click()}
          >
            <input id="call-bulk-inp" type="file" accept=".xlsx,.xls"
              style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(f)parseFile(f);}}/>
            {parsing
              ? <div style={{color:C.text3,fontSize:14}}>📖 Reading file...</div>
              : <>
                  <div style={{fontSize:44,marginBottom:12}}>📞</div>
                  <div style={{fontSize:15,fontWeight:700,color:C.text1,marginBottom:6}}>
                    Click or drag Excel file here
                  </div>
                  <div style={{fontSize:12,color:C.text3}}>Supports .xlsx · Max 10MB</div>
                </>
            }
          </div>

          {/* Column guide */}
          <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:14,
            padding:"18px 22px"}}>
            <div style={{fontWeight:700,fontSize:14,color:C.text1,marginBottom:12}}>
              📋 Required & Optional Columns
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.text3,
                  textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8}}>
                  Identifier (one required)
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <Tag color="blue">empCode</Tag>
                  <Tag color="blue">mobile</Tag>
                  <Tag color="blue">empEmail</Tag>
                </div>
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.text3,
                  textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8}}>
                  Required
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <Tag color="red">callKey *</Tag>
                  <Tag color="red">status *</Tag>
                </div>
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.text3,
                  textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8}}>
                  Optional
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <Tag>callDate</Tag>
                  <Tag>outcome</Tag>
                  <Tag>remarks</Tag>
                  <Tag>nextAction</Tag>
                </div>
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.text3,
                  textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8}}>
                  Q&A Answers (optional — hasQuestions calls)
                </div>
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  {DAY10_QS.map(q=><Tag key={q} style={{fontSize:10}}>{q}</Tag>)}
                </div>
              </div>
            </div>

            <div style={{marginTop:14,padding:"12px 14px",background:"#f5f8fc",
              borderRadius:10,border:`1px solid ${C.border}`}}>
              <div style={{fontWeight:700,fontSize:12,color:C.text2,marginBottom:6}}>
                <InfoCircleOutlined style={{marginRight:6,color:C.blue}}/>
                Valid values
              </div>
              <div style={{fontSize:12,color:C.text2}}>
                {(allSchedules||[]).map(s=>(
                  <React.Fragment key={s.scheduleKey}>
                    <strong>callKey ({s.label}):</strong>{' '}
                    {(s.stages||[]).map(st=>st.key).join(' · ')}<br/>
                  </React.Fragment>
                ))}
                <strong>status:</strong> {STATUSES.join(" · ")}<br/>
                <strong style={{color:C.text3}}>NA = Not Applicable (excluded from pending counts)</strong><br/>
                <strong>q1,q2,q4-q7,q9,q11,q12:</strong> Yes · No · NA<br/>
                <strong>q3, q8 (rating):</strong> 1-5 (decimals allowed e.g. 3.5)<br/>
                <strong>q10 (report style):</strong> Daily · Call by Call (case-insensitive)
              </div>
            </div>

            <div style={{display:"flex",gap:10,marginTop:14,flexWrap:"wrap"}}>
              {/* Dynamic template buttons — one per cohort schedule */}
              {(allSchedules||[]).map((sched,i) => {
                const colors=['#7c3aed',C.blue,C.teal,C.green,C.amber];
                const col = colors[i % colors.length];
                return (
                  <Button key={sched.scheduleKey}
                    icon={<DownloadOutlined/>}
                    onClick={()=>downloadTemplate(sched.scheduleKey)}
                    style={{borderColor:col,color:col}}>
                    📥 {sched.label} Template
                  </Button>
                );
              })}
              <Button icon={<DownloadOutlined/>} onClick={downloadQATemplate}
                style={{borderColor:C.green,color:C.green}}>
                📥 Q&A Template (All Cohorts)
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ══ STEP 1 — Preview ══ */}
      {step === 1 && (
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {/* Summary bar */}
          <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,
            padding:"14px 20px",display:"flex",alignItems:"center",
            justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <FileExcelOutlined style={{fontSize:28,color:C.green}}/>
              <div>
                <div style={{fontWeight:700,fontSize:14,color:C.text1}}>{file?.name}</div>
                <div style={{fontSize:12,color:C.text3}}>{rows.length} rows read</div>
              </div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              {/* Per callKey breakdown */}
              {CALL_KEYS.filter(k=>keyCounts[k]>0).map(k=>(
                <Tag key={k} color="blue" style={{borderRadius:20,fontWeight:600}}>
                  {k}: {keyCounts[k]}
                </Tag>
              ))}
              <Tag color="success" style={{borderRadius:20,fontWeight:600}}>
                ✓ {validCount} valid
              </Tag>
              {invalidCount>0 && (
                <Tag color="error" style={{borderRadius:20,fontWeight:600}}>
                  ✗ {invalidCount} errors
                </Tag>
              )}
            </div>
          </div>

          {/* Filter by callKey */}
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:12,color:C.text3,fontWeight:600}}>Filter by stage:</span>
            <Select value={filterKey} onChange={setFilterKey} size="small" style={{width:130}}
              options={[
                {value:"all",label:`All (${rows.length})`},
                ...CALL_KEYS.filter(k=>keyCounts[k]>0).map(k=>({
                  value:k, label:`${k} (${keyCounts[k]})`
                })),
              ]}/>
          </div>

          {/* Preview table */}
          <div style={{background:"#fff",border:`1px solid ${C.border}`,
            borderRadius:14,overflow:"hidden"}}>
            <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,
              fontWeight:600,fontSize:13,color:C.text1}}>
              <EyeOutlined style={{marginRight:8,color:C.blue}}/>
              Preview — {filteredRows.length} rows
              {filterKey!=="all" && ` (${filterKey} only)`}
            </div>
            <Table dataSource={filteredRows} columns={previewCols} rowKey="_row"
              size="small" scroll={{x:900}}
              pagination={{pageSize:15,showTotal:t=>`${t} rows`}}
              rowClassName={r=>r._errors?.length?"bulk-row-error":""} />
          </div>

          {/* Actions */}
          <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,
            padding:"14px 20px",display:"flex",justifyContent:"space-between",
            alignItems:"center",flexWrap:"wrap",gap:12}}>
            <div style={{fontSize:13,color:C.text2}}>
              {invalidCount>0
                ?`⚠️ ${invalidCount} rows will be skipped — fix and re-upload if needed`
                :`✅ All ${validCount} rows valid — ready to log`}
            </div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <Button onClick={()=>{setFile(null);setRows([]);setStep(0);}}>
                ← Choose Different File
              </Button>
              {invalidCount>0 && (
                <Button icon={<DownloadOutlined/>}
                  onClick={downloadErrorFile}
                  style={{borderColor:C.red,color:C.red}}>
                  📥 Download {invalidCount} Error Rows
                </Button>
              )}
              <Button type="primary" icon={<PhoneOutlined />}
                loading={loading} disabled={validCount===0}
                onClick={handleSubmit}
                style={{minWidth:220,fontWeight:600,background:C.navy}}>
                {loading?"Uploading...":`Log ${validCount} Calls`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ══ STEP 2 — Result ══ */}
      {step === 2 && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {error ? (
            <Alert type="error" showIcon message="Upload Failed" description={error}
              style={{borderRadius:12}}/>
          ) : (
            <>
              {/* Result hero */}
              <div style={{background:"#fff",border:`1px solid ${C.border}`,
                borderRadius:16,padding:"28px",textAlign:"center"}}>
                <div style={{fontSize:44,marginBottom:10}}>
                  {result?.failed===0?"✅":result?.success===0?"❌":"⚠️"}
                </div>
                <div style={{fontSize:18,fontWeight:800,color:C.text1,marginBottom:16}}>
                  {result?.failed===0
                    ?"All calls logged successfully!"
                    :`${result?.success??0} logged, ${result?.failed??0} failed`}
                </div>
                <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
                  {[
                    {label:"Total Rows",  value:result?.totalRows,            color:C.blue,   bg:"#eff6ff"},
                    {label:"✓ Success",   value:result?.success,              color:C.green,  bg:"#f0fdf4"},
                    ...(result?.warnings>0?[{label:"⚠ Resigned",value:result?.warnings,color:C.amber,bg:"#fffbeb"}]:[]),
                    {label:"✗ Failed",    value:result?.failed,               color:C.red,    bg:"#fef2f2"},
                  ].map(s=>(
                    <div key={s.label} style={{padding:"14px 28px",borderRadius:12,minWidth:110,
                      background:s.bg,border:`1px solid ${s.color}33`}}>
                      <div style={{fontSize:28,fontWeight:800,color:s.color,lineHeight:1}}>
                        {s.value??0}
                      </div>
                      <div style={{fontSize:11,fontWeight:700,color:C.text3,
                        textTransform:"uppercase",letterSpacing:"0.6px",marginTop:4}}>
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Error table */}
              {result?.errors?.length>0 && (
                <div style={{background:"#fff",border:"1.5px solid #fca5a5",
                  borderRadius:14,overflow:"hidden"}}>
                  <div style={{padding:"12px 16px",borderBottom:"1px solid #fecaca",
                    fontWeight:700,fontSize:13,color:C.red,background:"#fef2f2"}}>
                    <WarningOutlined style={{marginRight:8}}/>
                    Failed &amp; Warned Rows ({result.errors.length})
                    {result.warnings>0 && (
                      <span style={{fontSize:11,fontWeight:400,color:C.amber,marginLeft:12}}>
                        · {result.warnings} resigned-employee warnings (calls were still logged)
                      </span>
                    )}
                  </div>
                  <Table dataSource={result.errors} rowKey="row" size="small"
                    pagination={{pageSize:10}}
                    columns={[
                      {title:"Row",        dataIndex:"row",        width:60},
                      {title:"Identifier", dataIndex:"identifier", width:140,
                        render:v=><span style={{fontFamily:"monospace",fontWeight:600}}>{v}</span>},
                      {title:"Type",       dataIndex:"isWarning",  width:90, align:"center",
                        render:v=>v
                          ?<Tag color="warning" style={{borderRadius:20,fontWeight:700}}>⚠ Warning</Tag>
                          :<Tag color="error"   style={{borderRadius:20,fontWeight:700}}>✗ Failed</Tag>},
                      {title:"Reason",     dataIndex:"reason",
                        render:(v,rec)=><Tag color={rec.isWarning?"warning":"error"}
                          style={{whiteSpace:"normal"}}>{v}</Tag>},
                    ]}/>
                </div>
              )}
            </>
          )}

          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            <Button onClick={()=>{setStep(0);setFile(null);setRows([]);setResult(null);setError("");}}>
              Upload Another File
            </Button>
            {result?.errors?.length>0 && (
              <Button icon={<DownloadOutlined/>}
                onClick={downloadServerErrors}
                style={{borderColor:C.red,color:C.red}}>
                📥 Download {result.errors.length} Failed Rows
              </Button>
            )}
            <Button type="primary" style={{background:C.navy}}
              onClick={()=>navigate("/employees")}>
              Go to Employees
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}